import { Async, F1, F3, TMap } from '@client/new/helpers/types';
import {
  ContentMap,
  ContentRootTree,
  Content,
  ContentType,
  ExtrasContent,
} from './extras.domain';
import {
  useCreateLinkMutation,
  useCreateTabMutation,
  useReorderContentMutation,
  useUpdateTabMutation,
  useUpdateLinkMutation,
  useCreateLegacyGuideMutation,
  useUpdateLegacyGuideMutation,
  useGetContentQuery,
  useDeleteContentMutation,
  CreateLinkInput,
  CreateTabInput,
  CreateLegacyGuideInput,
  useGetProfileQuery,
  useCreateContentSlidesMutation,
  useCreateContentImageMutation,
  CreateImageInput,
  CreateSlidesInput,
  useUpdateSlidesMutation,
  GetContentQuery,
} from 'generated/graphql';
import { useAsyncQuery } from '@client/new/helpers/gql';
import { toExtrasContent, toProfileData } from './extras.mappers';
import { upload } from '@client/components/ajax';
import { _noop } from '@client/new/helpers/general';
import { ProfileData } from '@design-system/components/mobile-preview/types';

export type ExtrasModelParams = {
  creatorId: number;
};

export type ExtrasModel = {
  profile: Async<ProfileData>;
  data: Async<ExtrasContent>;
  reorder: F1<ExtrasContent['contentIds'], Promise<any>>;
  uploadFile: F1<File, Promise<string>>;
  create: F3<Content, string, File | undefined | null, Promise<any>>;
  update: F1<Content, Promise<any>>;
  delete: F1<Content, Promise<any>>;
};

export const useExtrasModel = (p: ExtrasModelParams): ExtrasModel => {
  const creatorId = String(p.creatorId);
  const q = useGetContentQuery({
    variables: { creatorId },
  });

  const profileQuery = useGetProfileQuery({
    variables: {
      id: Number(creatorId),
    },
  });

  const [reorderContentMutation] = useReorderContentMutation();
  const updateMutation = useUpdate(creatorId);
  const createMutation = useCreate(creatorId);
  const deleteMutation = useDelete(creatorId);

  type X = GetContentQuery['content'];
  const { data, refetch } = useAsyncQuery(q, 'content', toExtrasContent, {
    contentIds: { id: 'empty' },
    contentMap: {},
    childrenMap: {},
  });

  const { data: profile } = useAsyncQuery(
    profileQuery,
    'creator',
    toProfileData,
  );

  const uploadFile = async (file: File): Promise<string> => {
    const m = await upload(file, p.creatorId);

    return `${m.cdn01}${m.image}`;
  };

  const reorder: ExtrasModel['reorder'] = async (contentIds) => {
    if (!contentIds || !contentIds.id)
      return Promise.reject(new Error('Invalid content IDs'));

    await reorderContentMutation({
      variables: {
        creatorId,
        contentIds,
      },
    });
    return refetch();
  };

  const createContent: ExtrasModel['create'] = async (ct, parentId, file) => {
    if (data.type !== 'fetched') return;
    await createMutation({
      data: ct,
      tree: data.value.contentIds,
      contentMap: data.value.contentMap,
      parentId,
      file,
    });
    return refetch();
  };

  const updateContent: ExtrasModel['update'] = async (ct) => {
    if (data.type !== 'fetched') return;
    console.log('Updating content', ct);
    await updateMutation(ct);
    return refetch();
  };

  const deleteContent: ExtrasModel['delete'] = async (ct) => {
    if (data.type !== 'fetched') return;
    await deleteMutation({
      data: ct,
      tree: data.value.contentIds,
      contentMap: data.value.contentMap,
    });
    return refetch();
  };

  return {
    profile,
    data,
    reorder,
    uploadFile,
    create: createContent,
    update: updateContent,
    delete: deleteContent,
  };
};

const useCreate = (creatorId: string) => {
  const [createLinkMutation] = useCreateLinkMutation();
  const [createTabMutation] = useCreateTabMutation({});
  const [createLegacyGuideMutation] = useCreateLegacyGuideMutation();

  const [createContentSlidesMutation] = useCreateContentSlidesMutation();
  const [CreateContentImageMutation] = useCreateContentImageMutation();

  const createMutation = (d: Content, parentId: string, file?: File | null) => {
    const toVariables = <T>() => ({
      variables: {
        creatorId,
        input: { ...d.value, parentId } as T,
      },
    });

    switch (d.type) {
      case 'ContentLink':
        return createLinkMutation(toVariables<CreateLinkInput>()).then(
          (d) => d.data?.createLink,
        );
      case 'ContentTab':
        return createTabMutation(toVariables<CreateTabInput>()).then(
          (d) => d.data?.createTab,
        );
      case 'ContentLegacyGuide':
        return createLegacyGuideMutation(
          toVariables<CreateLegacyGuideInput>(),
        ).then((d) => d.data?.createLegacyGuide);

      case 'ContentSlides':
        return createContentSlidesMutation(
          toVariables<CreateSlidesInput>(),
        ).then((d) => d.data?.createSlides);

      case 'ContentImage':
        console.log('Creating image with file:', file);
        return CreateContentImageMutation({
          variables: {
            creatorId,
            input: { ...d.value, parentId } as CreateImageInput,
            file,
          },
        }).then((d) => d.data?.createImage);
    }
  };

  return async (p: CreateParams) => {
    const { data, file } = p;
    const res = await createMutation(data, p.parentId, file);
    if (!res?.id)
      return Promise.reject(new Error('Failed to create content ' + data.type));
  };
};

export const rootAllowedTypes: Array<ContentType> = ['ContentTab'];

export const contentHierarchyRules: Partial<TMap<ContentType, ContentType[]>> =
  {
    ['ContentTab']: ['ContentLegacyGuide', 'ContentLink'],
  };

export type CreateParams = {
  data: Content;
  tree: ContentRootTree;
  contentMap: ContentMap;
  parentId: string;
  file?: File | null;
};

const useUpdate = (creatorId: string) => {
  const [updateTabMutation] = useUpdateTabMutation();
  const [updateLinkMutation] = useUpdateLinkMutation();
  const [updateLegacyGuideMutation] = useUpdateLegacyGuideMutation();
  const [updateContentMutation] = useUpdateSlidesMutation();

  return async (d: Content) => {
    const { id: itemId, ...input } = d.value;
    const toVariables = () => ({
      variables: {
        creatorId,
        itemId,
        input,
      },
    });

    switch (d.type) {
      case 'ContentLink':
        return updateLinkMutation(toVariables());
      case 'ContentTab':
        return updateTabMutation(toVariables());
      case 'ContentLegacyGuide':
        return updateLegacyGuideMutation(toVariables());
      case 'ContentSlides':
        console.log('Updating slides', d);
        return updateContentMutation(toVariables()).then(
          (d) => d.data?.updateSlides,
        );
    }
  };
};

type DeleteParams = {
  data: Content;
  tree: ContentRootTree;
  contentMap: ContentMap;
};

const useDelete = (creatorId: string) => {
  const [deleteMutation] = useDeleteContentMutation();

  return async (d: DeleteParams) => {
    await deleteMutation({ variables: { creatorId, itemId: d.data.value.id } });
  };
};
